import React, { useEffect, useState } from "react";
import ClientLayout from "../../../components/client/ClientLayout";
import RequireClientAdmin from "../../../components/client/RequireClientAdmin";
import { clientApi } from "../../../api";

/**
 * Client-portal T&Cs configuration.
 *
 * Mirrors the admin venue Terms section: enable toggle, heading,
 * version label, body text, external link, and (when a URL is set)
 * the open-mode picker (new tab vs in-page modal — modal locks to
 * new_tab automatically for PDFs because most browsers won't iframe
 * them). The backend client settings allow-list mirrors these keys
 * verbatim, so this UI ships the same payload shape as the admin form.
 */
export default function SettingsTermsAndConditions() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <Inner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function Inner() {
  const [venue, setVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    clientApi.getVenue()
      .then((v) => setVenue({
        terms_required:     !!v.terms_required,
        terms_require_signature: v.terms_require_signature !== false,
        terms_title:        v.terms_title    || "",
        terms_body:         v.terms_body     || "",
        terms_url:          v.terms_url      || "",
        terms_version:      v.terms_version  || "",
        terms_display_mode: v.terms_display_mode || "new_tab",
      }))
      .catch((err) => setError(err.message));
  }, []);

  async function save() {
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload = { ...venue };
      // Mirror the admin-side defensive lock: PDFs can't be embedded.
      if (payload.terms_display_mode === "modal" && /\.pdf(\?|#|$)/i.test(payload.terms_url || "")) {
        payload.terms_display_mode = "new_tab";
      }
      await clientApi.updateVenue(payload);
      setSuccess("Terms saved.");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  // Distinguish "still loading" from "load failed" — otherwise a rejected
  // getVenue() leaves `venue` null forever and the page hangs on "Loading…".
  if (error && !venue) return <div style={{ maxWidth: 880 }}><div className="toast toast-error">{error}</div></div>;
  if (!venue) return <div style={{ maxWidth: 880 }}><div className="loading-state"><div className="spinner" /></div></div>;

  const isPdf = /\.pdf(\?|#|$)/i.test(venue.terms_url || "");
  const set = (k, v) => setVenue((cur) => ({ ...cur, [k]: v }));

  return (
    <div style={{ maxWidth: 880 }}>
      <div className="page-header">
        <h1>Terms &amp; Conditions</h1>
        <p className="page-subtitle">
          When enabled, members must read, tick, and sign an acknowledgement
          before they can pay. Acceptance is stored on the member record and in
          the activity log for audit.
        </p>
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {success && <div className="toast toast-success" style={{ marginBottom: "1rem" }}>{success}</div>}

      <div className="form-field" style={{ marginBottom: "1rem" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={venue.terms_required}
            onChange={(e) => set("terms_required", e.target.checked)}
          />
          <span>Require T&amp;Cs acknowledgement during public signup</span>
        </label>
      </div>

      <div className="form-field" style={{ marginBottom: "1rem" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: venue.terms_required ? "pointer" : "not-allowed", opacity: venue.terms_required ? 1 : 0.55 }}>
          <input
            type="checkbox"
            checked={venue.terms_require_signature !== false}
            onChange={(e) => set("terms_require_signature", e.target.checked)}
            disabled={!venue.terms_required}
          />
          <span>Require a signature (uncheck for a tick-only acknowledgement)</span>
        </label>
      </div>

      <div className="form-grid-2">
        <div className="form-field">
          <label>Heading</label>
          <input
            value={venue.terms_title}
            onChange={(e) => set("terms_title", e.target.value)}
            placeholder="Membership Terms & Conditions"
          />
          <span className="field-hint">
            Title shown at the top of the Terms step
          </span>
        </div>
        <div className="form-field">
          <label>Version</label>
          <input
            value={venue.terms_version}
            onChange={(e) => set("terms_version", e.target.value)}
            placeholder="v1 / 2026-05"
          />
          <span className="field-hint">
            Bump whenever the body text changes — captured on every acknowledgement.
          </span>
        </div>
      </div>

      <div className="form-field">
        <label>Body Text</label>
        <textarea
          rows={12}
          value={venue.terms_body}
          onChange={(e) => set("terms_body", e.target.value)}
          placeholder="Paste the full Terms & Conditions text here. Two blank lines separate paragraphs. Leave blank to use the External Link below instead."
          style={{ lineHeight: "1.5" }}
        />
        <span className="field-hint">
          If empty, the form will link out to the External Link below instead.
        </span>
      </div>

      <div className="form-field">
        <label>External Link {venue.terms_body ? "(optional)" : ""}</label>
        <input
          type="url"
          value={venue.terms_url}
          onChange={(e) => {
            const url = e.target.value;
            const pdf = /\.pdf(\?|#|$)/i.test(url);
            setVenue((cur) => ({ ...cur, terms_url: url, ...(pdf ? { terms_display_mode: "new_tab" } : {}) }));
          }}
          placeholder="https://yourvenue.com.au/membership-terms"
        />
        <span className="field-hint">
          {venue.terms_body
            ? "Shown below the body text — useful for linking the full PDF."
            : "Used as the primary Terms source when no Body Text is provided."}
        </span>
      </div>

      {venue.terms_url && (
        <div className="form-field">
          <label>How should the link open?</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.5rem", marginTop: "0.4rem" }}>
            <label style={{
              display: "flex", alignItems: "flex-start", gap: "0.6rem",
              padding: "0.7rem 0.85rem", borderRadius: "var(--radius-md)",
              border: `1px solid ${venue.terms_display_mode === "new_tab" ? "var(--accent)" : "var(--border)"}`,
              background: venue.terms_display_mode === "new_tab" ? "var(--accent-soft)" : "var(--bg-input)",
              cursor: "pointer",
            }}>
              <input type="radio" name="td_mode" value="new_tab"
                checked={venue.terms_display_mode === "new_tab"}
                onChange={() => set("terms_display_mode", "new_tab")}
                style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>New tab</div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  Opens in a fresh tab. Works for everything, including PDFs.
                </div>
              </div>
            </label>
            <label
              title={isPdf ? "PDFs cannot be embedded in a modal." : ""}
              style={{
                display: "flex", alignItems: "flex-start", gap: "0.6rem",
                padding: "0.7rem 0.85rem", borderRadius: "var(--radius-md)",
                border: `1px solid ${(!isPdf && venue.terms_display_mode === "modal") ? "var(--accent)" : "var(--border)"}`,
                background: (!isPdf && venue.terms_display_mode === "modal") ? "var(--accent-soft)" : "var(--bg-input)",
                cursor: isPdf ? "not-allowed" : "pointer",
                opacity: isPdf ? 0.55 : 1,
              }}>
              <input type="radio" name="td_mode" value="modal"
                checked={!isPdf && venue.terms_display_mode === "modal"}
                disabled={isPdf}
                onChange={() => set("terms_display_mode", "modal")}
                style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                  In-page modal {isPdf && <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(unavailable for PDFs)</span>}
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  Loads the link in an overlay so the member never leaves the form.
                </div>
              </div>
            </label>
          </div>
        </div>
      )}

      <div style={{ marginTop: "1.25rem", display: "flex", gap: "0.5rem" }}>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save Terms"}
        </button>
      </div>
    </div>
  );
}
